import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react"
import Head from "next/head"
import Image from "next/image"
import { MEDIA_QUERIES } from "@/constants/ui"
import { useOverflowSnapCarousel } from "@/hooks/use-overflow-snap-carousel"
import MarketingLayout from "@/layouts/marketing"
import { PublicPageProps } from "@/types/page"
import { cn } from "cn"
import { motion } from "motion/react"
import { useInView } from "react-intersection-observer"

import type {
  HomepageCta,
  HomepageCtaAction,
} from "@/lib/analytics/amplitude-types"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import {
  hasNoPlayableSource,
  HERO_VIDEO_TYPE,
  playOrReportRefusal,
} from "@/lib/hero-video"
import { Translate, useTranslation } from "@/lib/i18n/i18n-context"
import { Locale } from "@/lib/locale-hooks"
import { useRouter } from "@/lib/router"
import { useGetInspirationsStaticListQuery } from "@/lib/storage/api/inspiration-api"
import { NO_TRANSLATE } from "@/lib/weglot"
import IconArrowDown from "@/components/icons/arrow-down"
import IconArrowRight from "@/components/icons/arrow-right"
import IconCheck from "@/components/icons/check"
import IconPauseFill from "@/components/icons/pause-fill"
import IconPlane from "@/components/icons/plane"
import IconPlayFill from "@/components/icons/play-fill"
import IconStays from "@/components/icons/stays"
import { Container } from "../container"
import { InspirationCard } from "../inspiration-card"
import { BotAvatar } from "../ui/avatar"
import { Button } from "../ui/button"
import { IconButton } from "../ui/icon-button"
import {
  FeatureReveal,
  FeatureScene,
  FeatureSectionVideo,
  LazyBackgroundVideo,
  VoiceAssistantMobileIcon,
  VoiceAssistantOrb,
} from "./feature-scene"
import {
  MARKETING_CAROUSEL_CONTAINER_VARIANTS,
  MARKETING_CAROUSEL_IN_VIEW_OPTIONS,
  MARKETING_CAROUSEL_ITEM_VARIANTS,
  MarketingCarouselControls,
} from "./marketing-carousel"
import { MarketingEntryCtaButton } from "./marketing-entry-cta-button"
import { HighlightWords, ScrollHighlightGroup } from "./scroll-highlight-text"
import { SetupTravelAssistantButton } from "./setup-travel-assistant-button"

const FEATURED_GUIDES_PREVIEW_COUNT = 5

function trackHomepageCta(cta: HomepageCta, action?: HomepageCtaAction) {
  sendAnalyticsEvent({
    name: "ClickHomePageCta",
    properties: action ? { cta, action } : { cta },
  })
}

const HERO_VIDEO_NAMES = ["camels", "float", "van"] as const

const HERO_VIDEOS = HERO_VIDEO_NAMES.map((name) => ({
  src: `/img/${name}.webm`,
  portraitSrc: `/img/${name}-portrait.webm`,
  poster: `/img/${name}-poster.jpg`,
  portraitPoster: `/img/${name}-portrait-poster.jpg`,
}))

export const HERO_VIDEO_COUNT = HERO_VIDEOS.length

// Matches the incoming video's `duration-700`.
const HERO_FADE_MS = 700

function nextHeroIndex(index: number) {
  return (index + 1) % HERO_VIDEOS.length
}

function previousHeroIndex(index: number) {
  return (index + HERO_VIDEOS.length - 1) % HERO_VIDEOS.length
}

const FEATURE_SECTIONS = [
  { id: "learns", label: "Learns" },
  { id: "acts", label: "Acts" },
  { id: "adapts", label: "Adapts" },
] as const

type FeatureSectionId = (typeof FEATURE_SECTIONS)[number]["id"]

function FeatureSectionNav({
  activeSection,
}: {
  activeSection: FeatureSectionId
}) {
  return (
    <nav className="mb-10 flex justify-center gap-3 text-xs font-medium sm:h-14 sm:text-sm lg:justify-start">
      {FEATURE_SECTIONS.map(({ id, label }) => (
        <a
          key={id}
          href={`#${id}`}
          className={cn(
            "rounded-full border px-3 py-2 leading-none sm:border-2 sm:px-6 sm:py-4.5",
            activeSection === id ? "border-white-a-10" : "border-transparent"
          )}
          onClick={(e) => {
            e.preventDefault()
            document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })
          }}
        >
          {label}
        </a>
      ))}
    </nav>
  )
}

const FEATURE_HEADING_HIGHLIGHT_END = 0.25
const FEATURE_BODY_HIGHLIGHT_START = 0.3

function FeatureSectionCopy({
  heading,
  headingColor,
  children,
}: {
  heading: ReactNode
  headingColor: string
  children: ReactNode
}) {
  return (
    <ScrollHighlightGroup
      className={cn("mx-auto max-w-120 lg:ml-0", NO_TRANSLATE)}
    >
      <h2
        className="mb-6 font-circular text-5xl leading-11 text-balance sm:text-6xl sm:leading-13 md:text-7xl md:leading-15 lg:mb-12 lg:text-[6.75rem] lg:leading-21"
        style={{ color: headingColor }}
      >
        <HighlightWords to={FEATURE_HEADING_HIGHLIGHT_END}>
          {heading}
        </HighlightWords>
      </h2>
      <p className="text-sm leading-relaxed text-balance sm:text-md sm:text-pretty lg:text-lg">
        <HighlightWords from={FEATURE_BODY_HIGHLIGHT_START}>
          {children}
        </HighlightWords>
      </p>
    </ScrollHighlightGroup>
  )
}

function TravelAssistantFeatureCards() {
  const { scrollRef, canScroll, pages, activePageIndex, goTo, prev, next } =
    useOverflowSnapCarousel()
  const { ref: inViewRef, inView } = useInView(
    MARKETING_CAROUSEL_IN_VIEW_OPTIONS
  )

  return (
    <div
      ref={inViewRef}
      className="group/carousel relative ml-[calc(50%-50vw)] w-screen [--carousel-gap:theme(space.5)] [--section-inline-start:calc(var(--container-pad)+max(0px,(100vw-2*var(--container-pad)-var(--section-mw))/2))]"
    >
      <motion.div
        ref={scrollRef}
        animate={inView ? "visible" : "hidden"}
        variants={MARKETING_CAROUSEL_CONTAINER_VARIANTS}
        className={cn(
          "scrollbar-hidden flex scroll-pl-(--section-inline-start) gap-(--carousel-gap) overflow-y-hidden overscroll-x-contain pl-(--section-inline-start) *:shrink-0 *:basis-[calc((var(--section-mw)-3*var(--carousel-gap))/4)]",
          canScroll
            ? "snap-x snap-mandatory scroll-pr-(--section-inline-start) overflow-x-auto pr-(--section-inline-start) *:snap-start"
            : "overflow-x-hidden"
        )}
      >
        <motion.div variants={MARKETING_CAROUSEL_ITEM_VARIANTS}>
          <div className="rounded-2xl bg-white pt-10 pr-5 pb-16 pl-6">
            <svg width="24" height="24" fill="none">
              <path
                d="M12.0001 14.7636C12.7332 14.7636 13.4363 14.4724 13.9547 13.954C14.4731 13.4356 14.7643 12.7325 14.7643 11.9994C14.7643 11.2662 14.4731 10.5631 13.9547 10.0447C13.4363 9.52634 12.7332 9.23511 12.0001 9.23511C11.267 9.23511 10.5639 9.52634 10.0455 10.0447C9.52707 10.5631 9.23584 11.2662 9.23584 11.9994C9.23584 12.7325 9.52707 13.4356 10.0455 13.954C10.5639 14.4724 11.267 14.7636 12.0001 14.7636Z"
                stroke="black"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.3691 11.0614C20.877 11.613 20.877 12.3868 20.3691 12.9383C18.9746 14.4844 15.804 17.5764 12.0001 17.5764C8.19538 17.5764 5.02558 14.5947 3.63035 12.9383C3.12322 12.386 3.12322 11.613 3.63035 11.0614C5.02558 9.51537 8.19538 6.42334 12.0001 6.42334C15.804 6.42334 18.9746 9.51537 20.3691 11.0614Z"
                stroke="black"
                strokeWidth="1.3"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3 className="mb-2 font-circular text-3xl">Watch</h3>
            <p className="text-md text-pretty">
              Stay ahead with automatic alerts, or create agents to keep tabs on
              whatever matters to you.
            </p>
          </div>
          <Image
            src="/img/hp-watch.png"
            alt="Watch"
            width={260}
            height={288}
            unoptimized
            className="-mt-12 -mr-4 ml-auto"
          />
        </motion.div>
        <motion.div variants={MARKETING_CAROUSEL_ITEM_VARIANTS}>
          <div className="rounded-2xl bg-white pt-10 pr-5 pb-16 pl-6">
            <svg width="24" height="24" fill="none">
              <path
                d="M7.62069 7.10345V5.81034C7.62069 5.33021 7.81142 4.86974 8.15093 4.53024C8.49043 4.19073 8.9509 4 9.43103 4H13.569C14.0491 4 14.5096 4.19073 14.8491 4.53024C15.1886 4.86974 15.3793 5.33021 15.3793 5.81034V7.10345M7.62069 7.10345H5.81034C4.81052 7.10345 4 7.91397 4 8.91379V17.1897C4 18.1895 4.81052 19 5.81034 19H17.1897C18.1895 19 19 18.1895 19 17.1897V8.91379C19 7.91397 18.1895 7.10345 17.1897 7.10345H15.3793M7.62069 7.10345H15.3793"
                stroke="black"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3 className="mb-2 font-circular text-3xl">Build</h3>
            <p className="text-md text-pretty">
              Create a trip around how you travel and keep your itinerary, ideas
              and bookings all in one place.
            </p>
          </div>
          <div className="-mx-5 -mt-12">
            <Image
              src="/img/hp-build.png"
              alt="Build"
              width={325}
              height={215}
              unoptimized
            />
          </div>
        </motion.div>
        <motion.div variants={MARKETING_CAROUSEL_ITEM_VARIANTS}>
          <div className="min-h-67 rounded-2xl bg-white pt-10 pr-5 pb-16 pl-6">
            <svg width="24" height="24" fill="none">
              <path
                d="M12.6299 4.40153L14.6741 8.52868C14.7206 8.63343 14.7938 8.72417 14.8863 8.79184C14.9788 8.85952 15.0874 8.90177 15.2013 8.91439L19.7141 9.58296C19.8448 9.59976 19.968 9.65338 20.0693 9.73758C20.1707 9.82177 20.2459 9.93306 20.2864 10.0584C20.3268 10.1838 20.3308 10.3181 20.2977 10.4457C20.2647 10.5732 20.1961 10.6887 20.0999 10.7787L16.847 14.0058C16.764 14.0834 16.7017 14.1805 16.6657 14.2883C16.6298 14.396 16.6214 14.5111 16.6413 14.623L17.4256 19.1615C17.4483 19.2919 17.4339 19.4261 17.3841 19.5488C17.3344 19.6714 17.2511 19.7776 17.144 19.8553C17.0368 19.933 16.91 19.9791 16.7779 19.9883C16.6459 19.9975 16.5139 19.9694 16.397 19.9072L12.3341 17.7601C12.2301 17.709 12.1157 17.6825 11.9999 17.6825C11.884 17.6825 11.7696 17.709 11.6656 17.7601L7.60271 19.9072C7.48582 19.9694 7.35384 19.9975 7.22178 19.9883C7.08973 19.9791 6.9629 19.933 6.85573 19.8553C6.74856 19.7776 6.66534 19.6714 6.61556 19.5488C6.56578 19.4261 6.55143 19.2919 6.57414 19.1615L7.35842 14.5715C7.3783 14.4597 7.36989 14.3446 7.33396 14.2368C7.29804 14.129 7.23573 14.032 7.15271 13.9544L3.86128 10.7787C3.7639 10.6862 3.69542 10.5675 3.66413 10.437C3.63284 10.3064 3.64009 10.1695 3.68499 10.043C3.72989 9.91645 3.81052 9.80564 3.91712 9.72399C4.02371 9.64234 4.1517 9.59336 4.28557 9.58296L8.79842 8.91439C8.91233 8.90177 9.02095 8.85952 9.11344 8.79184C9.20593 8.72417 9.27907 8.63343 9.32557 8.52868L11.3699 4.40153C11.4255 4.28133 11.5144 4.17956 11.626 4.10824C11.7377 4.03692 11.8674 3.99902 11.9999 3.99902C12.1323 3.99902 12.262 4.03692 12.3737 4.10824C12.4853 4.17956 12.5742 4.28133 12.6299 4.40153Z"
                stroke="black"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3 className="mb-2 font-circular text-3xl">Book</h3>
            <p className="text-md text-pretty">
              Find and book flights, stays and restaurants that match your
              priorities.
            </p>
          </div>
          <Image
            src="/img/hp-book.png"
            alt="Book"
            width={260}
            height={194}
            unoptimized
            className="mx-auto -mt-11"
          />
        </motion.div>
        <motion.div variants={MARKETING_CAROUSEL_ITEM_VARIANTS}>
          <div className="min-h-67 rounded-2xl bg-white pt-10 pr-5 pb-16 pl-6">
            <svg width="24" height="24">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18.4798 4.20696C18.3492 4.22627 18.9399 4.03551 11.4778 6.46819C6.83302 7.98244 4.8447 8.6383 4.75875 8.68451C4.69159 8.7206 4.58829 8.79791 4.52916 8.8563C4.00734 9.3717 4.1092 10.2142 4.7389 10.591C4.80706 10.6318 6.06568 11.326 7.53583 12.1337C10.3905 13.7019 10.2911 13.6426 10.3803 13.8316C10.4024 13.8785 10.9509 15.0955 11.5991 16.5359C12.2474 17.9764 12.7923 19.1835 12.8101 19.2184C13.049 19.6873 13.6292 19.919 14.1347 19.7474C14.3744 19.666 14.6242 19.4508 14.7388 19.2269C14.8302 19.0484 19.7521 5.71811 19.7868 5.55523C19.8537 5.24068 19.7623 4.87778 19.5515 4.62081C19.286 4.29705 18.8941 4.14572 18.4798 4.20696ZM12.0854 7.63095C8.62288 8.76081 5.79598 9.69098 5.80338 9.69798C5.81078 9.705 6.94678 10.3319 8.32783 11.0912C9.70888 11.8504 10.8915 12.5088 10.9558 12.5541C11.121 12.6705 11.3017 12.8588 11.4135 13.0313C11.5174 13.1916 11.5319 13.223 12.8559 16.1698C13.3503 17.2703 13.7595 18.1654 13.7652 18.1588C13.7759 18.1465 18.3886 5.66368 18.4035 5.60638C18.4082 5.58853 18.405 5.57454 18.3964 5.5753C18.3878 5.57605 15.5479 6.50109 12.0854 7.63095Z"
                fill="black"
              />
            </svg>
            <h3 className="mb-2 font-circular text-3xl">Explore</h3>
            <p className="text-md text-pretty">
              Find great places, events and things to do based on where you are
              and what you’re into.
            </p>
          </div>
          <div className="-mx-1 -mt-15">
            <Image
              src="/img/hp-explore.png"
              alt="Explore"
              width={243}
              height={224}
              unoptimized
              className="ml-auto"
            />
          </div>
        </motion.div>
      </motion.div>
      <MarketingCarouselControls
        pages={pages}
        activePageIndex={activePageIndex}
        goTo={goTo}
        prev={prev}
        next={next}
        arrowClassName="top-28"
      />
    </div>
  )
}

interface HomepageTestimonial {
  tag: string
  quote: string
  name: string
  avatarColor: string
}

function testimonialInitials(name: string) {
  return name
    .split(/\s+/)
    .map((part) => part.replace(/\./g, "").charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase()
}

function TestimonialCard({
  tag,
  quote,
  name,
  avatarColor,
}: HomepageTestimonial) {
  return (
    <div className="flex flex-1 flex-col items-center rounded-5xl bg-white px-8 py-10 text-center sm:p-14">
      <div
        className={cn(
          "font-serif text-8xl leading-13.5 font-bold",
          // An ornament, not punctuation: Weglot would swap in „ for German
          NO_TRANSLATE
        )}
        aria-hidden="true"
      >
        “
      </div>
      <span className="rounded-full bg-black px-4 py-1.5 text-2xs leading-tight font-medium text-white">
        {tag}
      </span>
      <p className="my-auto py-10 text-md text-balance sm:text-lg">{quote}</p>
      <div className="flex items-center gap-2.5">
        <div
          className="flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-white text-xs font-semibold text-gray-12 shadow-sm"
          style={{ backgroundColor: avatarColor }}
          aria-hidden="true"
        >
          {testimonialInitials(name)}
        </div>
        <span className="text-md font-medium sm:text-lg">{name}</span>
      </div>
    </div>
  )
}

function TestimonialsCarousel() {
  const { scrollRef, canScroll, pages, activePageIndex, goTo, prev, next } =
    useOverflowSnapCarousel()
  const { ref: inViewRef, inView } = useInView(
    MARKETING_CAROUSEL_IN_VIEW_OPTIONS
  )

  return (
    <div
      ref={inViewRef}
      className="group/carousel relative ml-[calc(50%-50vw)] w-screen [--carousel-gap:theme(space.4)] [--section-inline-end:calc(var(--container-pad)+max(0px,(100vw-2*var(--container-pad)-var(--section-mw))/2))] [--section-inline-start-left:var(--section-inline-end)] sm:[--carousel-gap:theme(space.7)] sm:[--section-inline-start-left:calc(var(--section-inline-end)-theme(space.9))]"
    >
      <motion.div
        ref={scrollRef}
        animate={inView ? "visible" : "hidden"}
        variants={MARKETING_CAROUSEL_CONTAINER_VARIANTS}
        className={cn(
          "scrollbar-hidden flex scroll-pl-(--section-inline-start-left) items-stretch gap-(--carousel-gap) overflow-y-hidden overscroll-x-contain pl-(--section-inline-start-left)",
          canScroll
            ? "snap-x snap-mandatory scroll-pr-(--section-inline-end) overflow-x-auto pr-(--section-inline-end)"
            : "overflow-x-hidden"
        )}
      >
        {TESTIMONIALS.map((testimonial) => (
          <motion.div
            key={testimonial.name}
            className="flex shrink-0 basis-78 snap-start flex-col sm:basis-md"
            variants={MARKETING_CAROUSEL_ITEM_VARIANTS}
          >
            <TestimonialCard {...testimonial} />
          </motion.div>
        ))}
      </motion.div>
      <MarketingCarouselControls
        pages={pages}
        activePageIndex={activePageIndex}
        goTo={goTo}
        prev={prev}
        next={next}
      />
    </div>
  )
}

function FeaturedGuidesPreview() {
  const { ref: loadRef, inView: nearView } = useInView({
    triggerOnce: true,
    rootMargin: "50%",
  })
  const { ref: animateRef, inView: animateInView } = useInView(
    MARKETING_CAROUSEL_IN_VIEW_OPTIONS
  )
  const { data, isLoading } = useGetInspirationsStaticListQuery(
    { type: "featured", perPage: FEATURED_GUIDES_PREVIEW_COUNT },
    { skip: !nearView }
  )
  const guides = data?.slice(0, FEATURED_GUIDES_PREVIEW_COUNT) ?? []
  const showSkeletons = !nearView || (isLoading && guides.length === 0)
  const previewItemCount =
    guides.length +
    (showSkeletons ? FEATURED_GUIDES_PREVIEW_COUNT - guides.length : 0)
  const { scrollRef, canScroll, pages, activePageIndex, goTo, prev, next } =
    useOverflowSnapCarousel(previewItemCount)

  const setContainerRef = (el: HTMLDivElement | null) => {
    loadRef(el)
    animateRef(el)
  }

  return (
    <div
      ref={setContainerRef}
      className="group/carousel @container/card-grid relative ml-[calc(50%-50vw)] w-screen [--carousel-gap:theme(space.4)] [--section-inline-start:calc(var(--container-pad)+max(0px,(100vw-2*var(--container-pad)-var(--section-mw))/2))]"
    >
      <motion.ul
        ref={scrollRef}
        animate={animateInView ? "visible" : "hidden"}
        variants={MARKETING_CAROUSEL_CONTAINER_VARIANTS}
        className={cn(
          "scrollbar-hidden flex scroll-pl-(--section-inline-start) gap-(--carousel-gap) overflow-y-hidden overscroll-x-contain pl-(--section-inline-start) *:shrink-0 *:basis-58",
          canScroll
            ? "snap-x snap-mandatory scroll-pr-(--section-inline-start) overflow-x-auto pr-(--section-inline-start) *:snap-start"
            : "overflow-x-hidden"
        )}
      >
        {guides.map((inspiration) => (
          <motion.li
            key={inspiration.entity_ref}
            variants={MARKETING_CAROUSEL_ITEM_VARIANTS}
          >
            <InspirationCard
              inspiration={inspiration}
              isLinked
              showSave={false}
              showAddToTrip={false}
              slideshow={!canScroll}
              trackingContext="CardGrid"
            />
          </motion.li>
        ))}
        {showSkeletons &&
          Array.from({ length: FEATURED_GUIDES_PREVIEW_COUNT }).map(
            (_, index) => (
              <motion.li
                key={`loading-${index}`}
                variants={MARKETING_CAROUSEL_ITEM_VARIANTS}
              >
                <InspirationCard trackingContext="CardGrid" />
              </motion.li>
            )
          )}
      </motion.ul>
      <MarketingCarouselControls
        pages={pages}
        activePageIndex={activePageIndex}
        goTo={goTo}
        prev={prev}
        next={next}
        arrowClassName="top-28"
      />
    </div>
  )
}

function HeroVideoSources({
  index,
  onNoSource,
}: {
  index: number
  onNoSource: () => void
}) {
  const video = HERO_VIDEOS[index]
  if (!video) return null

  return (
    <>
      <source
        key={video.portraitSrc}
        src={video.portraitSrc}
        type={HERO_VIDEO_TYPE}
        media={MEDIA_QUERIES.xsMax}
      />
      {/* The last candidate failing means none can play. */}
      <source
        key={video.src}
        src={video.src}
        type={HERO_VIDEO_TYPE}
        onError={onNoSource}
      />
    </>
  )
}

function HeroVideoPoster({ index }: { index: number }) {
  const video = HERO_VIDEOS[index]
  if (!video) return null

  return (
    <picture>
      <source srcSet={video.portraitPoster} media={MEDIA_QUERIES.xsMax} />
      <Image
        src={video.poster}
        alt=""
        fill
        className="pointer-events-none z-2 object-cover object-center"
        unoptimized
      />
    </picture>
  )
}

interface HeroVideoPlaybackContextValue {
  paused: boolean
  togglePaused: () => void
  failed: boolean
}

const HeroVideoPlaybackContext =
  createContext<HeroVideoPlaybackContextValue | null>(null)

function useHeroVideoPlayback() {
  const value = useContext(HeroVideoPlaybackContext)
  if (!value) {
    throw new Error(
      "HeroVideoPauseControl must render inside HeroBackgroundVideos"
    )
  }
  return value
}

function HeroVideoPauseControl() {
  const { paused, togglePaused, failed } = useHeroVideoPlayback()
  if (failed) return null
  return (
    <IconButton
      variant="outline-clear"
      size="xs"
      icon={{
        icon: paused ? IconPlayFill : IconPauseFill,
        className: "text-[1em]",
      }}
      label={paused ? "Play background video" : "Pause background video"}
      onClick={togglePaused}
      className="absolute right-3 bottom-0.5 size-4.5 min-h-0 text-4xs text-gray-6 hover:text-foreground sm:right-0 sm:bottom-6 sm:size-6 sm:text-2xs sm:text-gray-7"
    />
  )
}

function HeroBackgroundVideos({
  initialIndex,
  children,
}: {
  initialIndex: number
  children: ReactNode
}) {
  const slot0Ref = useRef<HTMLVideoElement>(null)
  const slot1Ref = useRef<HTMLVideoElement>(null)
  const activeSlotRef = useRef<0 | 1>(0)
  const runningRef = useRef(true)
  const [activeIndex, setActiveIndex] = useState(initialIndex)
  const [activeSlot, setActiveSlot] = useState<0 | 1>(0)
  // The outgoing clip stays opaque beneath the incoming one until its fade-in
  // finishes, so nothing behind the hero ever shows through.
  const [fading, setFading] = useState(false)
  const incomingRef = useRef<"idle" | "starting" | "failed">("idle")
  // The next clip waits until the first one is playing with enough buffered,
  // so the two don't split the connection on first load and a clip that never
  // plays doesn't pull in the next.
  const [frontBuffered, setFrontBuffered] = useState(false)
  // The playing clip can't play here (no decodable source, a media error, or
  // autoplay refused); its first frame stands in.
  const [failed, setFailed] = useState(false)
  const [paused, setPaused] = useState(false)
  const { ref: inViewRef, inView } = useInView({
    initialInView: true,
  })

  const nextIndex = nextHeroIndex(activeIndex)
  // A new index remounts the slot's <video> (see its key), which would blank
  // the outgoing clip mid-fade.
  const backIndex = fading ? previousHeroIndex(activeIndex) : nextIndex
  const slot0Index = activeSlot === 0 ? activeIndex : backIndex
  const slot1Index = activeSlot === 1 ? activeIndex : backIndex
  activeSlotRef.current = activeSlot
  runningRef.current = inView && !paused

  // The SSR'd video starts loading before hydration, so its source errors may
  // have already fired.
  useEffect(() => {
    const front = slot0Ref.current
    if (front && hasNoPlayableSource(front)) setFailed(true)
  }, [])

  useEffect(() => {
    const front = activeSlot === 0 ? slot0Ref.current : slot1Ref.current
    if (!front) return
    if (inView && !paused) {
      playOrReportRefusal(front, () => setFailed(true))
    } else {
      front.pause()
    }
  }, [inView, paused, activeIndex, activeSlot])

  useEffect(() => {
    if (!fading) return
    const timeout = setTimeout(() => setFading(false), HERO_FADE_MS)
    return () => clearTimeout(timeout)
  }, [fading])

  function slotRef(slot: 0 | 1) {
    return slot === 0 ? slot0Ref : slot1Ref
  }

  function startIncoming(slot: 0 | 1) {
    const outgoing = slotRef(slot).current
    const incoming = slotRef(slot === 0 ? 1 : 0).current
    if (!incoming) return
    if (hasNoPlayableSource(incoming)) {
      incomingRef.current = "failed"
      return
    }
    incomingRef.current = "starting"
    incoming.play().catch(() => {
      incomingRef.current = "failed"
      if (outgoing?.ended) replayOutgoing(slot)
    })
  }

  // The next clip can't start, so this one loops and the next ending retries.
  function replayOutgoing(slot: 0 | 1) {
    incomingRef.current = "idle"
    void slotRef(slot)
      .current?.play()
      .catch(() => undefined)
  }

  // The next clip starts a fade's length early so both are moving while it
  // fades in.
  function handleTimeUpdate(slot: 0 | 1) {
    const video = slotRef(slot).current
    if (!video || slot !== activeSlotRef.current || !runningRef.current) {
      return
    }
    if (incomingRef.current !== "idle") return
    if (video.duration - video.currentTime <= HERO_FADE_MS / 1000) {
      startIncoming(slot)
    }
  }

  // An ended clip holds its last frame until the next one fires `playing`.
  function handleEnded(slot: 0 | 1) {
    if (slot !== activeSlotRef.current || !runningRef.current) return
    if (incomingRef.current === "idle") startIncoming(slot)
    if (incomingRef.current === "failed") replayOutgoing(slot)
  }

  function handlePlaying(slot: 0 | 1) {
    if (slot === activeSlotRef.current) {
      markFrontBuffered(slot)
      return
    }
    if (incomingRef.current !== "starting") return
    incomingRef.current = "idle"
    setActiveSlot(slot)
    setActiveIndex(nextHeroIndex)
    setFading(true)
  }

  function markFrontBuffered(slot: 0 | 1) {
    const video = slotRef(slot).current
    if (slot !== activeSlotRef.current || !video) return
    if (!video.paused && video.readyState >= video.HAVE_ENOUGH_DATA) {
      setFrontBuffered(true)
    }
  }

  function handleCannotPlay(slot: 0 | 1) {
    if (slot === activeSlotRef.current) setFailed(true)
  }

  function preloadFor(slot: 0 | 1) {
    return slot === activeSlot || (frontBuffered && !paused) ? "auto" : "none"
  }

  function togglePaused() {
    if (paused) {
      // Play inside the click, so a browser withholding autoplay has a gesture.
      playOrReportRefusal(slotRef(activeSlot).current, () => setFailed(true))
    } else {
      // A crossfade already under way would keep decoding both clips; clearing
      // the handoff lets the next clip end restart it.
      slot0Ref.current?.pause()
      slot1Ref.current?.pause()
      incomingRef.current = "idle"
    }
    setPaused(!paused)
  }

  function layerClassName(slot: 0 | 1) {
    if (slot === activeSlot) {
      return "z-1 opacity-100 transition-opacity duration-700 motion-reduce:transition-none"
    }
    return fading ? "opacity-100" : "opacity-0"
  }

  const playback = { paused, togglePaused, failed }

  return (
    <HeroVideoPlaybackContext.Provider value={playback}>
      <div className="absolute inset-x-0 top-0 h-[58%] sm:inset-0 sm:h-auto">
        <div
          ref={inViewRef}
          className="absolute inset-0 isolate overflow-hidden mask-[linear-gradient(to_bottom,black_68%,transparent)] sm:mask-none"
        >
          {/* Safari misplaces a reused <video>'s frame after its source changes,
            so each clip gets a fresh element. */}
          <video
            key={`0-${slot0Index}`}
            ref={slot0Ref}
            muted
            playsInline
            preload={preloadFor(0)}
            aria-hidden="true"
            onTimeUpdate={() => handleTimeUpdate(0)}
            onEnded={() => handleEnded(0)}
            onPlaying={() => handlePlaying(0)}
            onCanPlayThrough={() => markFrontBuffered(0)}
            onError={() => handleCannotPlay(0)}
            className={cn(
              "pointer-events-none absolute inset-0 size-full object-cover object-center",
              layerClassName(0)
            )}
          >
            <HeroVideoSources
              index={slot0Index}
              onNoSource={() => handleCannotPlay(0)}
            />
          </video>
          <video
            key={`1-${slot1Index}`}
            ref={slot1Ref}
            muted
            playsInline
            preload={preloadFor(1)}
            aria-hidden="true"
            onTimeUpdate={() => handleTimeUpdate(1)}
            onEnded={() => handleEnded(1)}
            onPlaying={() => handlePlaying(1)}
            onCanPlayThrough={() => markFrontBuffered(1)}
            onError={() => handleCannotPlay(1)}
            className={cn(
              "pointer-events-none absolute inset-0 size-full object-cover object-center",
              layerClassName(1)
            )}
          >
            <HeroVideoSources
              index={slot1Index}
              onNoSource={() => handleCannotPlay(1)}
            />
          </video>
          {failed && <HeroVideoPoster index={activeIndex} />}
        </div>
      </div>
      {children}
    </HeroVideoPlaybackContext.Provider>
  )
}

// Each "\n" in a translated title is a line break. A line keeps its own
// trailing space, so a title joined where breakClassName hides the <br> is
// spaced only in languages that use spaces. Returns plain nodes, not a
// component, so HighlightWords can still split the lines into words.
function withLineBreaks(text: string, breakClassName?: string) {
  const lines = text.split("\n")
  return lines.map((line, index) => (
    <Fragment key={index}>
      {line}
      {index < lines.length - 1 && <br className={breakClassName} />}
    </Fragment>
  ))
}

// A section heading that shrinks only when its longest word is wider than the
// column (a long compound like "Reiseassistenten."), measured off a hidden
// 1rem min-content copy; otherwise it renders at its class's size.
function FitHeading({ text, className }: { text: string; className: string }) {
  return (
    <div className={cn("@container flex items-start", className, NO_TRANSLATE)}>
      <div className="fit-text-box">
        <h2 className="fit-text font-circular">{text}</h2>
      </div>
      <span aria-hidden className="fit-text-ruler w-min font-circular">
        {text}
      </span>
    </div>
  )
}

// Circular has no CJK glyphs, and its tight tracking and leading collide the
// fallback font's. Read off the title itself so the server render, which knows
// the request's language, and the client agree.
const CJK_SCRIPT =
  /\p{Script=Han}|\p{Script=Hiragana}|\p{Script=Katakana}|\p{Script=Hangul}/u

// Phones break the title at its \n lines and wider screens keep it on one, and
// it shrinks until its widest line fits, measured off a hidden 1rem copy.
// font-circular sits on each copy, not the wrapper: its em tracking would
// otherwise inherit as px computed at the wrapper's size.
export function HeroTitle() {
  const t = useTranslation()
  const text = t("homeHeroTitle")
  const isCjk = CJK_SCRIPT.test(text)
  const title = withLineBreaks(text, "sm:hidden")

  return (
    <div
      className={cn(
        "@container mb-7 flex items-start px-6 text-[5.25rem] sm:mb-10 md:text-9xl",
        NO_TRANSLATE
      )}
    >
      <div className="fit-text-box">
        <h1
          className={cn(
            "fit-text text-balance",
            isCjk
              ? "leading-[1.15] font-semibold"
              : "font-circular leading-[.762] sm:leading-[.8125]"
          )}
        >
          {title}
        </h1>
      </div>
      <span
        aria-hidden
        className={cn(
          "fit-text-ruler whitespace-nowrap",
          isCjk ? "font-semibold" : "font-circular"
        )}
      >
        {title}
      </span>
    </div>
  )
}

export interface HomepageProps extends PublicPageProps {
  initialHeroIndex: number
}

export function Homepage({ session, initialHeroIndex }: HomepageProps) {
  const t = useTranslation()
  const formatMoney = Locale.useFormatMoney()
  const formatDayRange = Locale.useFormatDayObjectsAsRange()
  const init = useRef<boolean>(false)
  const learnsRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  useEffect(() => {
    if (!init.current) {
      init.current = true
      sendAnalyticsEvent({
        name: "ViewHomePage",
      })
    }
  }, [router.query])

  return (
    <MarketingLayout initUser={session?.user}>
      <Head>
        <title>{t("homeMetaTitle")}</title>
        <meta name="description" content={t("homeMetaDescription")} />
        <link rel="privacy-policy" href="https://mindtrip.ai/privacy-policy" />
      </Head>
      <div className="relative mb-11 flex min-h-[calc(100dvh+1.75rem)] flex-col overflow-hidden text-center sm:h-svh sm:min-h-200">
        <HeroBackgroundVideos initialIndex={initialHeroIndex}>
          <div className="pointer-events-none absolute inset-0 hidden bg-linear-to-b from-transparent from-47% to-white to-82% sm:block" />
          <div className="relative mt-auto">
            <div className="relative sm:contents">
              <HeroTitle />
              {/* Motion past 5s needs a stop control (WCAG 2.2.2) */}
              <HeroVideoPauseControl />
            </div>
            <Translate
              as="p"
              translationKey="homeHeroBody"
              className="mx-auto mb-5 max-w-[44em] px-4 text-sm leading-relaxed text-pretty text-black-a-9 sm:mb-6 sm:text-lg sm:text-balance"
            />
            <div>
              <SetupTravelAssistantButton
                variant="primary"
                initialProvider={session?.user?.provider}
                onClick={(action) => trackHomepageCta("hero", action)}
              />
            </div>
            <div className="flex h-[7svh] items-center justify-center">
              <IconButton
                variant="ghost"
                size="md"
                icon={{
                  icon: IconArrowDown,
                  className: "animate-bounce",
                }}
                label={t("learnMore")}
                className={NO_TRANSLATE}
                onClick={() =>
                  learnsRef.current?.scrollIntoView({
                    behavior: "smooth",
                  })
                }
              />
            </div>
          </div>
        </HeroBackgroundVideos>
      </div>
      <Container className="flex flex-col sm:gap-11">
        <FeatureScene
          id="learns"
          sectionRef={learnsRef}
          className="@container relative overflow-hidden rounded-t-6xl bg-[#142662] px-7 pt-20 text-center text-white max-sm:-mx-(--container-pad) max-sm:-scroll-mt-16 max-sm:motion-safe:scroll-flatten-t sm:rounded-6xl lg:h-200 lg:pt-40 lg:pb-30 lg:pl-[5%] lg:text-left xl:pl-[8%] 2xl:pl-36"
        >
          <FeatureSectionVideo
            src="/img/Learn.webm"
            className="max-xl:**:[video]:object-[40%] max-lg:**:[video]:object-[85%]"
          />
          <div className="relative">
            <FeatureSectionNav activeSection="learns" />
            <FeatureSectionCopy
              headingColor="#F9CDCC"
              heading={withLineBreaks(t("homeLearnsTitle"), "sm:hidden")}
            >
              {t("homeLearnsBody")}
            </FeatureSectionCopy>
          </div>
          <div className="relative h-128 text-left sm:h-170 lg:contents">
            <FeatureReveal
              delay={0}
              className="absolute top-4 -left-3 sm:top-8 sm:left-[2%] lg:top-12 lg:right-[25%] lg:left-auto xl:top-26"
            >
              <div className="flex w-fit animate-float1 flex-col gap-2 rounded-2xl px-4 py-2.5 text-xs font-medium text-white glass sm:px-5 sm:py-3 sm:text-md">
                <div className="flex items-center gap-2">
                  <BotAvatar size="2xs" />
                  <div className="h-2 w-12 rounded-full bg-white/10" />
                </div>
                Foodie spots, but kid-friendly
              </div>
            </FeatureReveal>
            <FeatureReveal
              delay={400}
              className="absolute top-24 right-5 sm:top-32 sm:right-auto sm:left-[10%] lg:top-36 lg:right-[10%] lg:left-auto xl:top-58 xl:right-24"
            >
              <div className="flex w-fit animate-float2 flex-col gap-2 rounded-2xl px-4 py-2.5 text-xs font-medium text-white glass sm:px-5 sm:py-3 sm:text-md">
                <div className="flex items-center gap-2">
                  <BotAvatar size="2xs" />
                  <div className="h-2 w-12 rounded-full bg-white/10" />
                </div>
                Street food over fine dining
              </div>
            </FeatureReveal>
            <FeatureReveal
              delay={800}
              className="absolute top-44 -right-5 sm:top-20 sm:right-[5%] lg:top-66 lg:right-[2%] xl:top-auto xl:right-[31%] xl:bottom-52 2xl:right-120"
            >
              <div className="flex w-fit animate-float3 flex-col gap-2 rounded-2xl px-4 py-2.5 text-xs font-medium text-white glass sm:px-5 sm:py-3 sm:text-md">
                <div className="flex items-center gap-2">
                  <BotAvatar size="2xs" />
                  <div className="h-2 w-12 rounded-full bg-white/10" />
                </div>
                Local gems only
              </div>
            </FeatureReveal>
          </div>
          <VoiceAssistant
            placement="learns"
            initialProvider={session?.user?.provider}
          />
        </FeatureScene>
        <FeatureScene
          id="acts"
          className="@container relative overflow-hidden bg-[#8D2406] px-7 pt-20 text-center text-white max-sm:-mx-(--container-pad) max-sm:-scroll-mt-16 sm:rounded-6xl lg:h-200 lg:pt-40 lg:pb-30 lg:pl-[5%] lg:text-left xl:pl-[8%] 2xl:pl-36"
        >
          <FeatureSectionVideo
            src="/img/Acts.webm"
            className="**:[video]:object-[88%] sm:**:[video]:object-right lg:**:[video]:object-[40%]"
          />
          <div className="relative">
            <FeatureSectionNav activeSection="acts" />
            <FeatureSectionCopy
              headingColor="#FFCEAB"
              heading={withLineBreaks(t("homeWorksTitle"))}
            >
              {t("homeWorksBody")}
            </FeatureSectionCopy>
          </div>
          <div className="relative h-128 text-left sm:h-170 lg:contents">
            <FeatureReveal
              delay={0}
              className="absolute top-5 left-1/2 -translate-x-1/2 sm:top-10 lg:top-14 lg:right-[3%] lg:left-auto lg:translate-none xl:top-28 xl:right-[15%] 2xl:right-72"
            >
              <div className="flex w-[21em] animate-float1 flex-col gap-2 rounded-3xl px-4 py-2.5 text-xs font-semibold glass sm:px-5 sm:py-3 sm:text-md">
                <div className="flex items-center gap-2">
                  <BotAvatar size="xs" className="max-sm:size-5" />
                  <div className="h-2 w-12 rounded-full bg-white/10" />
                </div>
                <div className="ml-0.5 flex items-start gap-2">
                  <IconCheck className="mt-0.5 size-3.5 stroke-3 sm:mt-1.25" />
                  <span>Somewhere warm, flexible dates</span>
                </div>
              </div>
            </FeatureReveal>
            <FeatureReveal
              delay={400}
              className="absolute top-25 -left-3 sm:top-40 sm:left-[.5%] md:left-[1%] lg:right-[29%] lg:left-auto xl:top-66 xl:right-[42%] 2xl:right-150"
            >
              <div className="flex w-34 animate-float2 flex-col gap-2 rounded-3xl px-4 py-2.5 text-xs font-semibold glass sm:w-44 sm:px-5 sm:py-3 sm:text-md">
                <div className="h-2 w-12 rounded-full bg-white/10" />
                <div className="ml-0.5 flex items-start gap-2">
                  <IconCheck className="mt-0.5 size-3.5 stroke-3 sm:mt-1.25" />
                  <span>
                    Direct flight, under{" "}
                    <span className="no-translate">
                      {formatMoney(600, false)}
                    </span>
                  </span>
                </div>
              </div>
            </FeatureReveal>
            <FeatureReveal
              delay={800}
              className="absolute top-26 -right-4 sm:top-42 sm:right-[.5%] md:right-[1%] lg:top-66 lg:right-[36%] xl:right-[2%] 2xl:right-20"
            >
              <div className="flex w-36 animate-float3 flex-col gap-2 rounded-3xl px-4 py-2.5 text-xs font-semibold glass sm:w-44 sm:px-5 sm:py-3 sm:text-md">
                <div className="h-2 w-12 rounded-full bg-white/10" />
                <div className="ml-0.5 flex items-start gap-2">
                  <IconCheck className="mt-0.5 size-3.5 stroke-3 sm:mt-1.25" />
                  <span>Aisle seat with carry-on</span>
                </div>
              </div>
            </FeatureReveal>
            <FeatureReveal
              delay={1200}
              className="absolute bottom-32 flex justify-center max-lg:left-1/2 max-lg:w-full max-lg:max-w-120 max-lg:-translate-x-1/2 sm:bottom-44 lg:max-xl:hidden xl:right-[4%] 2xl:right-32"
            >
              <Image
                src="/img/acts-flight.png"
                alt="Flight options"
                width={600}
                height={147}
                unoptimized
                className="mr-[-8%]"
              />
            </FeatureReveal>
          </div>
          <VoiceAssistant
            placement="acts"
            initialProvider={session?.user?.provider}
          />
        </FeatureScene>
        <FeatureScene
          id="adapts"
          className="@container relative flex flex-col overflow-hidden rounded-b-6xl bg-[#434717] px-7 pt-20 text-center text-white max-sm:-mx-(--container-pad) max-sm:-scroll-mt-16 max-sm:motion-safe:scroll-flatten-b sm:rounded-6xl lg:h-200 lg:pt-40 lg:pb-30 lg:pl-[5%] lg:text-left xl:pl-[8%] 2xl:pl-36"
        >
          <FeatureSectionVideo
            src="/img/Adapts.webm"
            className="max-sm:object-[80%]"
          />
          <div className="relative z-1">
            <FeatureSectionNav activeSection="adapts" />
            <FeatureSectionCopy
              headingColor="#F1F2BA"
              heading={t("homeAdaptsTitle")}
            >
              {t("homeAdaptsBody")}
            </FeatureSectionCopy>
          </div>
          <div className="relative h-128 sm:h-170 lg:contents">
            <div className="absolute top-3 bottom-0 left-1/2 flex w-68 -translate-x-1/2 flex-col rounded-t-[3.75rem] border-x-4 border-t-4 border-white-a-3 bg-black-a-4 text-left sm:top-6 sm:w-104 lg:top-18 lg:right-[12.5%] lg:left-auto lg:translate-none">
              <FeatureReveal
                delay={0}
                className="absolute top-4 -left-7 sm:top-16 sm:-left-14 lg:-top-12 lg:left-8 xl:top-12 xl:-left-9"
              >
                <div className="max-w-[13.5em] animate-float1 rounded-2xl p-3 text-3xs glass sm:text-sm">
                  <div className="mb-2.5 flex items-start justify-between">
                    <Image
                      src="/img/adapts-airline.png"
                      alt="Airline logo"
                      width={56}
                      height={56}
                      unoptimized
                      className="overflow-hidden rounded-xl max-sm:size-8 max-sm:rounded-md"
                    />
                    <div className="h-2 w-7 rounded-full bg-white/10" />
                  </div>
                  <div className="flex items-center gap-1 font-semibold">
                    <IconPlane className="size-[1.5em]" />
                    <span>SFO-LAX</span>
                  </div>
                  <div className="sm:text-xs">
                    <div>
                      <span className="no-translate">
                        {formatDayRange(
                          DEMO_FLIGHT_DATES.from,
                          DEMO_FLIGHT_DATES.to
                        )}
                      </span>{" "}
                      · Roundtrip
                    </div>
                    <div>
                      <span className="no-translate font-normal text-white-a-8 line-through">
                        {formatMoney(598, false)}
                      </span>{" "}
                      <span className="no-translate font-semibold text-green-8">
                        {formatMoney(389, false)}
                      </span>{" "}
                      · <span className="font-semibold">Price drop</span>
                    </div>
                  </div>
                </div>
              </FeatureReveal>
              <FeatureReveal
                delay={400}
                className="absolute top-9 -right-8 sm:top-6 sm:-right-10 lg:top-12 lg:-right-12 xl:top-26"
              >
                <div className="flex w-fit max-w-[13.5em] animate-float2 flex-col rounded-2xl p-3 text-3xs glass sm:text-sm">
                  <div className="mb-2.5 flex items-start justify-between">
                    <Image
                      src="/img/adapts-hotel.jpg"
                      alt="Hotel photo"
                      width={56}
                      height={56}
                      className="overflow-hidden rounded-xl max-sm:size-8 max-sm:rounded-md"
                    />
                    <div className="h-2 w-7 rounded-full bg-white/10" />
                  </div>
                  <div className="flex items-center gap-1 font-semibold">
                    <IconStays className="size-[1.5em]" />
                    <span>Hotel</span>
                  </div>
                  <div className="sm:text-xs">
                    <div className="no-translate">
                      {formatDayRange(
                        DEMO_HOTEL_DATES.from,
                        DEMO_HOTEL_DATES.to
                      )}{" "}
                      · {t("nightCountOther", { count: 4 })}
                    </div>
                    <div>
                      <span className="no-translate font-normal text-white-a-8 line-through">
                        {formatMoney(398, false)}
                      </span>{" "}
                      <span className="no-translate font-semibold text-green-8">
                        {formatMoney(289, false)}
                      </span>{" "}
                      · <span className="font-semibold">Price drop</span>
                    </div>
                  </div>
                </div>
              </FeatureReveal>
              <FeatureReveal
                delay={800}
                className="absolute top-41 left-1/2 -translate-x-1/2 sm:top-56 sm:-right-20 sm:left-auto sm:translate-none lg:top-auto lg:bottom-86 xl:-right-22 xl:bottom-60"
              >
                <div className="flex w-76 animate-float3 flex-col gap-2 rounded-3xl px-4 py-2.5 text-sm font-semibold text-white glass sm:w-88 sm:px-5 sm:py-3 sm:text-md">
                  <div className="flex items-center gap-2">
                    <BotAvatar size="xs" className="max-sm:size-5" />
                    <div className="h-2 w-12 rounded-full bg-white/10" />
                  </div>
                  Hey Elsa, looks like it’s going to rain tomorrow. Here are
                  some indoor ideas:
                  <div className="flex flex-col items-start gap-2">
                    <div className="h-2 w-[50%] rounded-full bg-white/10" />
                    <div className="h-2 w-[70%] rounded-full bg-white/10" />
                    <div className="hidden h-2 w-[30%] rounded-full bg-white/10 sm:block" />
                  </div>
                </div>
              </FeatureReveal>
            </div>
            <VoiceAssistant
              placement="adapts"
              initialProvider={session?.user?.provider}
              className="max-sm:px-0"
            />
          </div>
        </FeatureScene>
      </Container>
      <div className="bg-linear-to-b from-white to-black-a-2 px-(--container-pad) py-30 [--section-mw:75rem] sm:rounded-6xl">
        <div className="mx-auto max-w-(--section-mw) text-center sm:text-left">
          <FitHeading
            text={t("homeDoMoreTitle")}
            className="mb-6 text-5xl leading-11 text-balance sm:text-[5rem] sm:leading-none"
          />
          <p className={cn("mb-11 text-pretty sm:text-lg", NO_TRANSLATE)}>
            {t("homeDoMoreBody")}
          </p>
        </div>
        <TravelAssistantFeatureCards />
      </div>
      <div className="px-(--container-pad) pt-30 pb-30 [--section-mw:75rem]">
        <div className="mx-auto max-w-(--section-mw) text-center sm:text-left">
          <FitHeading
            text={t("homeShapedTitle")}
            className="mb-6 text-5xl leading-11 text-balance sm:mb-12 sm:text-[5rem] sm:leading-none"
          />
          <Translate
            as="p"
            translationKey="homeShapedBody"
            className="mb-11 max-w-4xl text-pretty sm:text-lg"
          />
          <FeaturedGuidesPreview />
          <div
            className={cn(
              "mt-9 flex items-center justify-center sm:justify-start",
              NO_TRANSLATE
            )}
          >
            <Button
              variant="primary"
              size="2xl"
              href="/inspiration"
              onClick={() => trackHomepageCta("explore_guides")}
            >
              {t("homeExploreGuides")}
            </Button>
            <div className="hidden sm:block">
              <Button
                variant="none"
                size="2xl"
                href="/creator-program"
                iconAfter={IconArrowRight}
                className="transition-colors hover:text-gray-11 **:[svg]:transition-transform hover:**:[svg]:translate-x-0.5"
                onClick={() => trackHomepageCta("creator_community")}
              >
                {t("homeJoinCreatorCommunity")}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-linear-to-b from-white to-black-a-2 px-(--container-pad) pt-10 pb-30 [--section-mw:75rem] sm:rounded-6xl sm:pt-20">
        <div className="mx-auto max-w-(--section-mw) text-center sm:text-left">
          <FitHeading
            text={t("homeTestimonialsTitle")}
            className="mb-12 text-5xl leading-11 text-balance sm:mb-16 sm:text-[5rem] sm:leading-none"
          />
        </div>
        <TestimonialsCarousel />
        <div className="mx-auto mt-20 max-w-(--section-mw) text-center sm:mt-32 sm:text-left">
          <FitHeading
            text={t("homeFeaturedInTitle")}
            className="mb-20 text-5xl leading-11 text-balance sm:mb-24 sm:text-[5rem] sm:leading-none"
          />
          <div className="flex flex-col items-center gap-12 sm:grid sm:grid-cols-2 sm:items-center sm:justify-start sm:justify-items-start sm:gap-x-16 sm:gap-y-20 lg:grid-cols-3">
            {PRESS_LOGOS.map((logo) => (
              <a
                key={logo.alt}
                href={logo.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-fit shrink-0 items-center justify-center transition-opacity hover:opacity-70"
              >
                <Image
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width}
                  height={logo.height}
                  className={cn("shrink-0 max-sm:w-auto", logo.className)}
                  unoptimized
                />
              </a>
            ))}
          </div>
        </div>
      </div>
      <Container className="max-sm:px-0 sm:my-11">
        <div className="relative flex flex-col overflow-hidden bg-black pt-96 pb-24 text-center text-white sm:rounded-6xl sm:pt-120">
          <LazyBackgroundVideo src="/img/Setup.webm" />
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0.1)),linear-gradient(1deg,rgba(0,0,0,0.72)_30.81%,rgba(0,0,0,0)_65.77%)]" />
          <div className={cn("relative px-6 sm:px-16", NO_TRANSLATE)}>
            <FitHeading
              text={t("homeClosingTitle")}
              className="mb-4 text-5xl leading-11 text-balance sm:mb-6 sm:text-[5rem] sm:leading-18"
            />
            <p className="mx-auto mb-6 max-w-4xl text-md text-pretty sm:text-lg">
              {t("homeClosingBody")}
            </p>
            <div>
              <SetupTravelAssistantButton
                variant="knockout"
                initialProvider={session?.user?.provider}
                onClick={(action) => trackHomepageCta("closing", action)}
              />
            </div>
          </div>
        </div>
      </Container>
    </MarketingLayout>
  )
}

function VoiceAssistant({
  placement,
  initialProvider,
  className,
}: {
  placement: HomepageCta
  initialProvider?: string
  className?: string
}) {
  return (
    <div
      className={cn(
        "absolute inset-x-0 bottom-9 flex justify-center max-sm:px-7 lg:right-[12.5%] lg:bottom-14 lg:left-auto lg:w-104",
        className
      )}
    >
      <MarketingEntryCtaButton
        variant="knockout"
        size="xl"
        block
        initialProvider={initialProvider}
        className="gap-0 px-2.5 text-base **:data-[slot=setup-label]:flex-1 sm:hidden"
        onClick={(action) => trackHomepageCta(placement, action)}
      >
        <VoiceAssistantMobileIcon />
      </MarketingEntryCtaButton>
      <VoiceAssistantOrb
        initialProvider={initialProvider}
        className="max-sm:hidden"
        onClick={(action) => trackHomepageCta(placement, action)}
      />
    </div>
  )
}

const DEMO_FLIGHT_DATES = {
  from: { year: 2026, month: 9, day: 22 },
  to: { year: 2026, month: 9, day: 24 },
}
const DEMO_HOTEL_DATES = {
  from: { year: 2026, month: 9, day: 17 },
  to: { year: 2026, month: 9, day: 21 },
}

const TESTIMONIALS: HomepageTestimonial[] = [
  {
    tag: "Remembers Your Preferences",
    quote:
      "I love that it learns my preferences and the convenience of not having to repeat things.",
    name: "Michael M.",
    avatarColor: "#F9CDCC",
  },
  {
    tag: "Clear Reasoning",
    quote:
      "I like that my travel assistant doesn’t just make recommendations — it explains why they’re a good fit.",
    name: "Damian S.",
    avatarColor: "#E87B5C",
  },
  {
    tag: "Easy Planning",
    quote:
      "I can find places and keep all my trip details in one place, so I don’t have to search for everything again.",
    name: "Julie A.",
    avatarColor: "#E8C547",
  },
  {
    tag: "Better Decisions",
    quote:
      "Mindtrip doesn’t just agree with my ideas and plans — it gives real feedback.",
    name: "Dana S.",
    avatarColor: "#B8E6B0",
  },
  {
    tag: "Hidden Gems",
    quote:
      "Mindtrip has helped us find great off-the-beaten-path places and activities.",
    name: "Alison M.",
    avatarColor: "#A8D8EA",
  },
]

const PRESS_LOGOS = [
  {
    href: "https://www.nytimes.com/2024/06/26/travel/norway-artficial-intelligence-planners.html",
    src: "/img/logo-nytimes.svg",
    alt: "The New York Times",
    width: 290,
    height: 40,
    className: "max-sm:h-8",
  },
  {
    href: "https://techcrunch.com/2023/09/07/mindtrip-ai-travel-agent/",
    src: "/img/logo-techcrunch.svg",
    alt: "TechCrunch",
    width: 86,
    height: 45,
    className: "max-sm:h-9",
  },
  {
    href: "https://www.youtube.com/watch?v=0EsYM4_q8JY&t=96",
    src: "/img/logo-today.svg",
    alt: "Today",
    width: 220,
    height: 33,
    className: "max-sm:h-6.5",
  },
  {
    href: "https://www.bloomberg.com/news/articles/2025-05-15/five-new-tools-to-save-you-time-and-money-on-summer-travel",
    src: "/img/logo-bloomberg.svg",
    alt: "Bloomberg",
    width: 162,
    height: 30,
    className: "max-sm:h-6",
  },
  {
    href: "https://www.cnet.com/tech/services-and-software/mindtrips-ai-flight-agent-review/",
    src: "/img/logo-cnet.svg",
    alt: "CNET",
    width: 149,
    height: 33,
    className: "max-sm:h-6.5",
  },
  {
    href: "https://ktla.com/morning-news/this-new-site-could-rival-google-flights/",
    src: "/img/logo-ktla.svg",
    alt: "KTLA 5",
    width: 71,
    height: 90,
    className: "max-sm:h-18",
  },
]
